import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CryptoError, decodeLinkKey, openSecret } from '@sss/crypto';
import type { RoomEndReason } from '@sss/types';
import FlameMark from '../components/Brand/FlameMark';
import CopyButton from '../components/Secret/CopyButton';
import secretStyles from '../components/Secret/Secret.module.css';
import { usePageTitle } from '../hooks/usePageTitle';
import { ICE_SERVERS } from '../lib/config';
import { HandoffConnection, type Transport } from '../lib/handoffConnection';
import { formatCode } from '../lib/handoffProtocol';
import { getRoom, roomSocketUrl } from '../lib/roomsApi';
import { forgetFragment, readFragment } from '../lib/secretLinks';
import { SecretsApiError } from '../lib/secretsApi';
import ui from '../styles/ui.module.css';
import styles from './LivePage.module.css';
import revealStyles from './RevealPage.module.css';

/**
 * /h/:id#<roomKey> — receive a live handoff (docs/adr/0010). Loading the
 * page only checks the room is open; connecting waits for the Receive click,
 * so link-preview bots can't take the recipient's seat. The payload is a v1
 * secret sealed under the room key, opened here like a burn link.
 */

type Live = { phase: 'connecting' | 'waitingSender' | 'securing' | 'receiving'; code: string | null; transport: Transport | null };

type State =
  | { step: 'checking' }
  | { step: 'incomplete' }
  | { step: 'gone' }
  | { step: 'ready' }
  | { step: 'live'; live: Live }
  | { step: 'locked'; payload: Uint8Array; wrong: boolean }
  | { step: 'revealed'; text: string }
  | { step: 'ended'; reason: Exclude<RoomEndReason, 'acknowledged' | 'delivered'> | 'lost' }
  | { step: 'failed'; reason: 'network' | 'rateLimited' | 'undecryptable' | 'other' };

export default function HandoffPage() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const [state, setState] = useState<State>({ step: 'checking' });
  const [passphrase, setPassphrase] = useState('');
  // After the secret arrives: whether we've acknowledged, and whether the sender is still there to hear it.
  const [acknowledged, setAcknowledged] = useState(false);
  const [senderGone, setSenderGone] = useState(false);
  const roomKey = useRef<Uint8Array | null>(null);
  const connection = useRef<HandoffConnection | null>(null);
  const received = useRef(false);
  const heading = useRef<HTMLHeadingElement>(null);
  usePageTitle(t('pageTitle.handoff'));

  useEffect(() => {
    try {
      const fragment = readFragment(window.location.hash);
      roomKey.current = fragment ? decodeLinkKey(fragment) : null;
    } catch {
      roomKey.current = null;
    }
    if (!roomKey.current) {
      setState({ step: 'incomplete' });
      return;
    }
    let cancelled = false;
    getRoom(id)
      .then(() => !cancelled && setState({ step: 'ready' }))
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof SecretsApiError && err.type === 'gone') {
          forgetFragment();
          setState({ step: 'gone' });
        } else setState({ step: 'failed', reason: err instanceof SecretsApiError && err.type === 'rateLimited' ? 'rateLimited' : 'network' });
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => () => connection.current?.close(), []);

  useEffect(() => {
    if (state.step === 'revealed' || state.step === 'gone' || state.step === 'ended' || state.step === 'failed') heading.current?.focus();
  }, [state.step]);

  const updateLive = (patch: Partial<Live>) => setState((current) => (current.step === 'live' ? { step: 'live', live: { ...current.live, ...patch } } : current));

  async function open(payload: Uint8Array, typed?: string) {
    const key = roomKey.current;
    if (!key) return;
    try {
      const text = await openSecret(payload, key, { passphrase: typed || undefined });
      roomKey.current = null;
      forgetFragment();
      setPassphrase('');
      setState({ step: 'revealed', text });
    } catch (err) {
      if (err instanceof CryptoError && (err.code === 'passphraseRequired' || (err.code === 'decryptionFailed' && typed))) {
        setState({ step: 'locked', payload, wrong: err.code === 'decryptionFailed' });
      } else {
        forgetFragment();
        setState({ step: 'failed', reason: 'undecryptable' });
      }
    }
  }

  function receive() {
    const key = roomKey.current;
    if (!key) return;
    setState({ step: 'live', live: { phase: 'connecting', code: null, transport: null } });
    const conn = new HandoffConnection({
      roomId: id,
      roomKey: key,
      role: 'recipient',
      socketUrl: roomSocketUrl(id, 'recipient', window.location.origin),
      iceServers: ICE_SERVERS,
      events: {
        joined: () => updateLive({ phase: 'waitingSender' }),
        peer: (present) => {
          if (received.current) {
            if (!present) setSenderGone(true);
          } else updateLive(present ? { phase: 'securing', code: null, transport: null } : { phase: 'waitingSender', code: null, transport: null });
        },
        secure: (code) => updateLive({ phase: 'receiving', code }),
        transport: (transport) => updateLive({ transport }),
        record: (type, body) => {
          // One secret per handoff; a resend after a reconnect is the same one.
          if (type !== 'secret' || received.current) return;
          received.current = true;
          void conn.send('delivered');
          void open(body);
        },
        ended: (reason) => {
          if (received.current) setSenderGone(true);
          else if (reason !== 'acknowledged' && reason !== 'delivered') setState({ step: 'ended', reason });
          else setState({ step: 'ended', reason: 'lost' });
        },
        unavailable: () => {
          if (received.current) setSenderGone(true);
          else {
            forgetFragment();
            setState({ step: 'gone' });
          }
        },
        lost: () => {
          if (received.current) setSenderGone(true);
          else setState({ step: 'ended', reason: 'lost' });
        },
      },
    });
    connection.current = conn;
    conn.connect();
  }

  async function onUnlock(e: React.FormEvent, payload: Uint8Array) {
    e.preventDefault();
    await open(payload, passphrase);
  }

  async function gotIt() {
    try {
      await connection.current?.send('acknowledged');
    } catch {
      setSenderGone(true);
      return;
    }
    setAcknowledged(true);
  }

  const liveLine = (live: Live) => t(`handoff.${live.phase}`);

  return (
    <main className={revealStyles.page}>
      {state.step === 'checking' && (
        <p className={ui.hint} role="status">
          {t('handoff.checking')}
        </p>
      )}

      {state.step === 'incomplete' && <Message heading={t('reveal.incomplete.title')} body={t('reveal.incomplete.body')} headingRef={heading} />}

      {state.step === 'gone' && <Message heading={t('handoff.gone.title')} body={t('handoff.gone.body')} headingRef={heading} />}

      {state.step === 'ended' && <Message heading={t('handoff.ended.title')} body={t(`handoff.ended.${state.reason}`)} headingRef={heading} />}

      {state.step === 'failed' && <Message heading={t('handoff.failed.title')} body={t(`handoff.failed.${state.reason}`)} headingRef={heading} />}

      {(state.step === 'ready' || state.step === 'live') && (
        <section className={revealStyles.stack} aria-labelledby="handoff-title">
          <h1 id="handoff-title" className={revealStyles.title} ref={heading} tabIndex={-1}>
            {t('handoff.titleLine1')} <span className={revealStyles.accent}>{t('handoff.titleLine2')}</span>
          </h1>
          <p className={revealStyles.lead}>{t('handoff.lead')}</p>
          {state.step === 'ready' ? (
            <>
              <button type="button" className={ui.button} onClick={receive}>
                <FlameMark size={18} tone="onAccent" />
                {t('handoff.button')}
              </button>
              <div className={styles.notes}>
                <p className={ui.hint}>{t('handoff.botNote')}</p>
                <p className={ui.hint}>{t('handoff.ipNote')}</p>
              </div>
            </>
          ) : (
            <section className={styles.status} aria-label={t('handoff.label')}>
              <p className={styles.statusLine} role="status" data-testid="handoff-status">
                <span className={`${styles.dot} ${styles.dotWaiting}`} aria-hidden="true" />
                {liveLine(state.live)}
              </p>
              {state.live.code && (
                <div className={styles.code}>
                  <span className={ui.eyebrow}>{t('handoff.code')}</span>
                  <span className={styles.codeDigits} data-testid="handoff-code">
                    {formatCode(state.live.code)}
                  </span>
                  <span className={ui.hint}>{t('handoff.codeHint')}</span>
                </div>
              )}
            </section>
          )}
        </section>
      )}

      {state.step === 'locked' && (
        <form className={revealStyles.stack} onSubmit={(e) => onUnlock(e, state.payload)}>
          <h1 className={revealStyles.title} ref={heading} tabIndex={-1}>
            {t('reveal.locked.title')}
          </h1>
          {state.wrong && (
            <p role="alert" className={ui.error}>
              {t('reveal.locked.wrong')}
            </p>
          )}
          <p className={secretStyles.notice}>{t('reveal.locked.keepTab')}</p>
          <label className={ui.field}>
            {t('reveal.passphraseLabel')}
            <input type="password" value={passphrase} onChange={(e) => setPassphrase(e.target.value)} autoComplete="off" autoFocus required />
          </label>
          <button type="submit" className={ui.button} disabled={!passphrase}>
            {t('reveal.locked.unlock')}
          </button>
        </form>
      )}

      {state.step === 'revealed' && (
        <section className={revealStyles.stack} aria-labelledby="handoff-revealed-title">
          <h1 id="handoff-revealed-title" className={revealStyles.title} ref={heading} tabIndex={-1}>
            {t('handoff.revealedTitle')}
          </h1>
          {/* Scrolls when long, so it must take keyboard focus. */}
          <pre className={secretStyles.secretText} data-testid="handoff-text" tabIndex={0} aria-labelledby="handoff-revealed-title">
            {state.text}
          </pre>
          <div className={secretStyles.actions}>
            <CopyButton value={state.text} className={ui.buttonSecondary} />
            {!acknowledged && !senderGone && (
              <button type="button" className={ui.button} onClick={() => void gotIt()} aria-describedby="got-it-hint">
                {t('handoff.gotIt')}
              </button>
            )}
          </div>
          <p className={secretStyles.notice} role="status" id="got-it-hint">
            {acknowledged ? t('handoff.done') : senderGone ? t('handoff.senderGone') : t('handoff.gotItHint')}
          </p>
        </section>
      )}
    </main>
  );
}

function Message({ heading, body, headingRef }: { heading: string; body: string; headingRef: React.RefObject<HTMLHeadingElement | null> }) {
  return (
    <section className={revealStyles.stack}>
      <h1 className={revealStyles.title} ref={headingRef} tabIndex={-1}>
        {heading}
      </h1>
      <p className={revealStyles.lead}>{body}</p>
    </section>
  );
}
